import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { isEmpty } from 'lodash';
import styled from '@emotion/styled';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    HeadingText50 as StepTitle,
    pxToRem,
} from '@modernhealth/vital-components';

import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { mediaQueries } from 'src/shared/styles/variables';

import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { CareNavigationRequest } from '../models/careNavigation';
import { config } from 'src/config/config';
import { useAddCareNavigationRequestMutation } from '../documents/addCareNavigationRequestMutation.generated';

import {
    CareNavigationRequestContactPreference,
    CareNavigationRequestSource,
    CareNavigationRequestTopic,
    DeviceCategory,
} from 'src/generated/webapp_gql';
import { ButtonGroupTestID } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(40),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
    maxWidth: '100%',
});

const ParagraphContainer = styled.div({
    paddingTop: pxToRem(16),
});

const FormContainer = styled.div({
    textAlign: 'left',
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

export const CareNavigationDisclaimerStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();
    const [addCareNavigationRequestMutation] = useAddCareNavigationRequestMutation();

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    if (isEmpty(location.state)) {
        navigationService.navigate('/care-navigation-request');
    }

    const { t } = useTranslation();
    const requestState = location.state;

    const handleBack = () => {
        navigationService.navigate('/care-navigation-contact-information', requestState);
    };

    let secondButtonText: string;

    if (location.state.contactPreference === CareNavigationRequestContactPreference.Phone) {
        secondButtonText = t(
            'careNavigation:care_navigation_contact_information_step.request_call',
        );
    } else {
        secondButtonText = t(
            'careNavigation:care_navigation_contact_information_step.request_email',
        );
    }

    const handleRequest = () => {
        addCareNavigationRequestMutation({
            variables: {
                message: requestState.message,
                contactPreference:
                    requestState.contactPreference as CareNavigationRequestContactPreference,
                topic: requestState.topic as CareNavigationRequestTopic,
                source: requestState.source as CareNavigationRequestSource,
                contactPhone:
                    requestState.contactPreference ===
                        CareNavigationRequestContactPreference.Phone &&
                    requestState.contactPhone !== undefined
                        ? requestState.contactPhone
                        : '',
                contactEmail:
                    requestState.contactPreference === CareNavigationRequestContactPreference.Email
                        ? requestState.contactEmail
                        : '',
                updatePhone:
                    requestState.contactPreference ===
                        CareNavigationRequestContactPreference.Phone && requestState.updatePhone,
                inAppConsent: true,
                platform: requestState.platform as DeviceCategory,
                subtopicsOfFocus:
                    requestState.topic === CareNavigationRequestTopic.TopicsOfFocus
                        ? requestState.subTopics
                        : [],
            },
        })
            .then(async response => {
                if (response?.data?.addCareNavigationRequest?.ok) {
                    navigationService.navigate('/care-navigation-all-set', requestState);
                } else {
                    toastNotificationsService.renderErrorToast(config.messages.genericError);
                }
            })
            .catch(error => toastNotificationsService.renderErrorToast(error));
    };

    return (
        <Wrapper>
            <CareNavigationSteps
                step={requestState.flow === 'singleClinical' ? 4 : 5}
                flow={requestState.flow}
            />

            <TitleContainer>
                <StepTitle>{t('careNavigation:care_navigation_disclaimer.title')}</StepTitle>
            </TitleContainer>
            <BodyTextMedium bold>
                {t('careNavigation:care_navigation_disclaimer.what_is_carenav')}
            </BodyTextMedium>
            <BodyTextMedium>
                {t('careNavigation:care_navigation_disclaimer.what_is_answer')}
            </BodyTextMedium>
            <ParagraphContainer>
                <BodyTextMedium bold>
                    {t('careNavigation:care_navigation_disclaimer.how_carenav')}
                </BodyTextMedium>
                <BodyTextMedium>
                    {t('careNavigation:care_navigation_disclaimer.how_carenav_answer')}
                </BodyTextMedium>
            </ParagraphContainer>

            <ParagraphContainer>
                <BodyTextMedium bold>
                    {t('careNavigation:care_navigation_disclaimer.what_will')}
                </BodyTextMedium>
                <BodyTextMedium>
                    {t('careNavigation:care_navigation_disclaimer.what_will_answer_part_1')}
                </BodyTextMedium>
            </ParagraphContainer>

            <ParagraphContainer>
                <BodyTextMedium>
                    {requestState.contactPreference === CareNavigationRequestContactPreference.Phone
                        ? t(
                              'careNavigation:care_navigation_disclaimer.what_will_answer_part_2_call',
                          )
                        : t(
                              'careNavigation:care_navigation_disclaimer.what_will_answer_part_2_email',
                          )}
                </BodyTextMedium>
            </ParagraphContainer>

            <FormContainer>
                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleRequest}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t(secondButtonText)}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </FormContainer>
        </Wrapper>
    );
};
